import { liveQuery } from "dexie";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { getGatewayBaseUrl } from "../runtime/config";
import {
  CashSaleVoidGatewayClient,
  type CashSaleVoidSyncResult,
} from "../runtime/gateway/cash-sale-void-client";
import type { LocalCashSaleSnapshot } from "../runtime/local-store/cash-sale-state";
import { readLocalEmployeeSession, type LocalEmployeeSessionState } from "../runtime/local-store/employee-session";
import { posRuntimeDb } from "../runtime/local-store/pos-db";
import { readLocalShift, type LocalShiftState } from "../runtime/local-store/shift-state";
import {
  readTerminalCredential,
  type TerminalCredentialState,
} from "../runtime/local-store/terminal-state";

type RuntimeState = {
  terminal: TerminalCredentialState | null;
  session: LocalEmployeeSessionState | null;
  shift: LocalShiftState | null;
  sale: LocalCashSaleSnapshot | null;
};

function formatMoney(value: number, currency = "THB") {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function requestKey(orderId: string) {
  const suffix = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `pos-cash-void:${orderId}:${suffix}`;
}

function friendlyError(value: string) {
  if (value.includes("POS_CASH_VOID_REQUIRES_ORIGINAL_EMPLOYEE_V1")) return "รอบนี้ให้เฉพาะพนักงานคนเดิมที่ขายบิลนี้เป็นผู้ยกเลิก";
  if (value.includes("POS_CASH_VOID_ORIGINAL_SHIFT_NOT_OPEN")) return "บิลนี้ไม่ได้อยู่ในกะเดิมที่ยังเปิดอยู่ จึงยกเลิกแบบ Full VOID รอบนี้ไม่ได้";
  if (value.includes("POS_CASH_VOID_CLAIM_REVERSAL_NOT_SUPPORTED_V1")) return "บิลนี้ใช้คูปองที่มีการใช้สิทธิ์แล้ว ต้องรอ Correction Contract ที่คืนสิทธิ์ได้ครบ";
  if (value.includes("POS_CASH_VOID_REQUIRES_SETTLED_CASH_PAYMENT") || value.includes("POS_CASH_VOID_REQUIRES_CASH_SALE_V1")) return "Full VOID รอบนี้รองรับเฉพาะบิลเงินสดที่ชำระสำเร็จเท่านั้น";
  if (value.includes("POS_CASH_VOID_PERMISSION_REQUIRED") || value.includes("COMMERCE_SALE_COMMIT_PERMISSION_REQUIRED")) return "พนักงานคนนี้ไม่มีสิทธิ์ทำรายการยกเลิกบิลรอบนี้";
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID")) return "เซสชันพนักงานหมดอายุ กรุณา Login ใหม่";
  if (value.includes("REQUEST_KEY_CONFLICT")) return "คำขอยกเลิกเดิมไม่ตรงกับรายการนี้ กรุณาปิดแล้วเปิด Review ใหม่";
  if (value.includes("GATEWAY_TIMEOUT") || value.includes("GATEWAY_UNREACHABLE")) return "ยังยืนยันการยกเลิกกับ Core ไม่สำเร็จ กดลองอีกครั้งโดยระบบจะใช้ Request Key เดิม";
  return value || "ยกเลิกบิลไม่สำเร็จ";
}

export function CashSaleVoidRuntimeHost() {
  const [runtime, setRuntime] = useState<RuntimeState>({
    terminal: null,
    session: null,
    shift: null,
    sale: null,
  });
  const [mountNode, setMountNode] = useState<Element | null>(null);
  const [statusText, setStatusText] = useState("");
  const [online, setOnline] = useState(navigator.onLine);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [voidRequestKey, setVoidRequestKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CashSaleVoidSyncResult | null>(null);

  useEffect(() => {
    const subscription = liveQuery(async () => {
      const [terminal, session, shift, rows] = await Promise.all([
        readTerminalCredential(),
        readLocalEmployeeSession(),
        readLocalShift(),
        posRuntimeDb.journal.where("kind").equals("SALE_ORDER").toArray(),
      ]);

      const identity = terminal?.identity ?? null;
      const sale = rows
        .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
        .map((row) => row.snapshot as LocalCashSaleSnapshot)
        .find((snapshot) =>
          snapshot?.schema === "LOCAL_CASH_SALE_V1" &&
          Boolean(snapshot.canonical) &&
          Boolean(identity) &&
          snapshot.organizationId === identity?.organization_id &&
          snapshot.branchId === identity?.branch_id &&
          snapshot.nodeId === identity?.node_id,
        ) ?? null;

      return { terminal, session, shift, sale } satisfies RuntimeState;
    }).subscribe({
      next: setRuntime,
      error: () => setRuntime({ terminal: null, session: null, shift: null, sale: null }),
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const update = () => {
      setMountNode(document.querySelector(".cash-sale-modal__actions"));
      setStatusText(document.querySelector(".cash-sale-modal__status")?.textContent ?? "");
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  const sale = runtime.sale;
  const canonical = sale?.canonical ?? null;
  const currentOrderDisplayed = Boolean(canonical?.order_no && statusText.includes(canonical.order_no));
  const sameEmployee = Boolean(runtime.session?.actor.employee_id && runtime.session.actor.employee_id === sale?.performingEmployeeId);
  const sameOpenShift = Boolean(
    runtime.shift?.shift.shift_status === "OPEN" &&
    runtime.shift.shift.shift_id === sale?.shiftId,
  );
  const settledCash = Boolean(canonical?.payment?.payment_method_code === "CASH" && canonical.payment.payment_status === "SETTLED");
  const canVoid = Boolean(
    mountNode &&
    currentOrderDisplayed &&
    sameEmployee &&
    sameOpenShift &&
    settledCash &&
    canonical?.order_status === "PAID",
  );

  const amount = useMemo(() => canonical?.grand_total ?? sale?.localTotal ?? 0, [canonical, sale]);
  const currency = canonical?.currency_code ?? sale?.currencyCode ?? "THB";

  function openReview() {
    if (!canonical || !canVoid) return;
    setReason("");
    setError(null);
    setResult(null);
    setVoidRequestKey(requestKey(canonical.order_id));
    setReviewOpen(true);
  }

  async function confirmVoid() {
    if (!canonical || !sale || !runtime.terminal || !runtime.session || !canVoid) return;
    const normalizedReason = reason.trim();
    if (!normalizedReason) {
      setError("กรุณาระบุเหตุผลที่ยกเลิกบิล");
      return;
    }
    if (!online) {
      setError("การยกเลิกบิลต้อง Online เพื่อให้ Order, Payment, Cash Shift และ Stock กลับพร้อมกัน");
      return;
    }

    const baseUrl = getGatewayBaseUrl();
    if (!baseUrl) {
      setError("POS Gateway ยังไม่พร้อมใช้งาน");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const client = new CashSaleVoidGatewayClient({ baseUrl });
      const response = await client.voidSale({
        deviceSecret: runtime.terminal.deviceSecret,
        sessionToken: runtime.session.sessionToken,
        orderId: canonical.order_id,
        reason: normalizedReason,
        requestKey: voidRequestKey || requestKey(canonical.order_id),
        clientContext: {
          runtime: "WEBPOS",
          correction_type: "FULL_VOID",
          local_transaction_id: sale.localTransactionId,
          original_sale_request_key: sale.rootRequestKey,
          performing_employee_id: sale.performingEmployeeId,
          shift_id: sale.shiftId,
          local_commit_authority: false,
        },
      });

      if (!response.ok || !response.data) {
        throw new Error(response.error ?? "POS_CASH_SALE_VOID_FAILED");
      }
      setResult(response.data);
    } catch (voidError) {
      setError(friendlyError(voidError instanceof Error ? voidError.message : "POS_CASH_SALE_VOID_FAILED"));
    } finally {
      setBusy(false);
    }
  }

  if (!mountNode || !currentOrderDisplayed || !settledCash) return null;

  return (
    <>
      {createPortal(
        result ? (
          <button className="cash-sale-modal__secondary" type="button" disabled>
            ยกเลิกแล้ว · คืนผลกระทบสำเร็จ
          </button>
        ) : (
          <button
            className="cash-sale-modal__secondary"
            type="button"
            disabled={!canVoid || !online}
            onClick={openReview}
          >
            ยกเลิกบิลนี้
          </button>
        ),
        mountNode,
      )}

      {reviewOpen ? createPortal(
        <div className="cash-sale-modal-backdrop" role="presentation">
          <section className="cash-sale-modal" role="dialog" aria-modal="true" aria-labelledby="cash-sale-void-title">
            <div className="cash-sale-modal__header">
              <div>
                <p>ตรวจสอบก่อนทำรายการ</p>
                <h2 id="cash-sale-void-title">{result ? "ยกเลิกบิลสำเร็จ" : "ยกเลิกบิลเงินสด"}</h2>
              </div>
              {!busy ? (
                <button className="cash-sale-modal__close" type="button" onClick={() => setReviewOpen(false)} aria-label="ปิด">×</button>
              ) : null}
            </div>

            <div className="cash-sale-modal__total">
              <p>{canonical?.order_no}</p>
              <strong>{formatMoney(amount, currency)}</strong>
            </div>

            {result ? (
              <>
                <div className="cash-sale-modal__status">
                  Order และ Payment เป็น VOIDED แล้ว · Cash Shift ถูกปรับยอดกลับ · Stock คืนตาม Movement เดิม {result.stock_reversal_count} ชุด
                </div>
                <div className="cash-sale-modal__destination">
                  <p>เหตุผล</p>
                  <strong>{result.reason}</strong>
                  <p>ยอดเงินสดคาดหมายหลังยกเลิก: {formatMoney(Number(result.expected_cash), result.currency_code)}</p>
                  <p>ยอดยกเลิกสะสมในกะ: {formatMoney(Number(result.void_total), result.currency_code)}</p>
                </div>
              </>
            ) : (
              <>
                <div className="cash-sale-modal__destination">
                  <strong>ผลที่จะเกิดพร้อมกัน</strong>
                  <p>Order → VOIDED</p>
                  <p>Payment เงินสด → VOIDED</p>
                  <p>ยอดเงินสดคาดหมายของกะ → ลดกลับตามยอดบิล</p>
                  <p>Stock → Reverse จาก Movement เดิม ไม่คำนวณสูตรใหม่</p>
                </div>

                <label className="cash-sale-modal__field">
                  <span>เหตุผลที่ยกเลิกบิล</span>
                  <input
                    type="text"
                    maxLength={500}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="เช่น ลูกค้าขอยกเลิกรายการ"
                    autoFocus
                  />
                </label>

                {error ? <div className="cash-sale-modal__status cash-sale-modal__status--error">{error}</div> : null}
              </>
            )}

            <div className="cash-sale-modal__actions">
              {result ? (
                <button className="cash-sale-modal__primary" type="button" onClick={() => setReviewOpen(false)}>ปิด</button>
              ) : (
                <>
                  <button className="cash-sale-modal__secondary" type="button" disabled={busy} onClick={() => setReviewOpen(false)}>ไม่ยกเลิก</button>
                  <button
                    className="cash-sale-modal__primary"
                    type="button"
                    disabled={busy || !reason.trim() || !online}
                    onClick={() => void confirmVoid()}
                  >
                    {busy ? "กำลังคืนผลกระทบกับ Core…" : "ยืนยันยกเลิกบิล"}
                  </button>
                </>
              )}
            </div>
          </section>
        </div>,
        document.body,
      ) : null}
    </>
  );
}
